/* Nomsa surrounded by noise — orbital motion. Photorealistic background + code-animated cards.
 * No end-text overlay. Content: emails, X, Facebook, Instagram — illustrating suspicious links,
 * message analysis, and manipulated media detection cues.
 */
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";

const BG = "/prologue/nomsa_bg_clean.png";

// Each ring orbits around Nomsa (center ~ 50%, 62% of the frame).
// Angle in degrees for starting position; radius in vw-ish px units (relative to 1600 canvas).
// We compose each card's transform with a rotating parent to get true circular orbit.
const ORBIT_CARDS = [
  { ring: 0, startDeg:   0, kind: "email",     w: 260 },
  { ring: 0, startDeg: 120, kind: "twitter",   w: 260 },
  { ring: 0, startDeg: 240, kind: "instagram", w: 240 },
  { ring: 1, startDeg:  60, kind: "facebook",  w: 260 },
  { ring: 1, startDeg: 200, kind: "whatsapp",  w: 240 },
  { ring: 1, startDeg: 320, kind: "adviser",   w: 240 },
];

// Ring configuration: radiusX / radiusY in px, rotation duration seconds
const RINGS = [
  { rx: 460, ry: 260, duration: 26, direction:  1 },  // inner
  { rx: 680, ry: 360, duration: 36, direction: -1 },  // outer (counter-rotating)
];

// Center of orbit (percent of canvas)
const CX = 50;
const CY = 60;

export default function NomsaNoiseOrbitDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";

  const scene = (
    <div
      className="relative w-full h-full overflow-hidden"
      data-testid="nomsa-noise-orbit"
      style={{ background: "#050914" }}
    >
      {/* Photorealistic background */}
      <img
        src={BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: "center 42%" }}
      />
      {/* Vignette to focus attention on Nomsa */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 60%, transparent 30%, rgba(5,9,20,0.55) 75%, rgba(5,9,20,0.9) 100%)" }} />

      {/* Warm rim glow behind Nomsa */}
      <div className="absolute pointer-events-none"
        style={{
          left: `${CX}%`, top: `${CY}%`, transform: "translate(-50%, -50%)",
          width: 520, height: 520, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,190,110,0.20) 0%, rgba(255,140,60,0.08) 40%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      {/* Orbital cards */}
      {RINGS.map((ring, ri) => (
        <motion.div
          key={ri}
          className="absolute"
          style={{ left: `${CX}%`, top: `${CY}%`, width: 0, height: 0 }}
          animate={{ rotate: 360 * ring.direction }}
          transition={{ duration: ring.duration, repeat: Infinity, ease: "linear" }}
        >
          {ORBIT_CARDS.filter(c => c.ring === ri).map((card, ci) => {
            const rad = (card.startDeg * Math.PI) / 180;
            const x = Math.cos(rad) * ring.rx;
            const y = Math.sin(rad) * ring.ry;
            return (
              <motion.div
                key={ci}
                className="absolute"
                style={{
                  left: 0, top: 0,
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                  width: card.w,
                }}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 + ci * 0.15 + ri * 0.4 }}
              >
                {/* Counter-rotate so card text stays upright while ring spins */}
                <motion.div
                  animate={{ rotate: -360 * ring.direction }}
                  transition={{ duration: ring.duration, repeat: Infinity, ease: "linear" }}
                >
                  {/* subtle bob */}
                  <motion.div
                    animate={{ y: [0, -6, 0, 6, 0] }}
                    transition={{ duration: 5 + ci, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <CardBody kind={card.kind} />
                  </motion.div>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      ))}
    </div>
  );

  if (embed) {
    return (
      <div className="w-screen h-screen" style={{ background: "#050914" }}>
        {scene}
      </div>
    );
  }
  return (
    <div className="w-full" style={{ background: "#050914", minHeight: "100vh" }}>
      <div className="mx-auto" style={{ maxWidth: 1600, aspectRatio: "16/9" }}>{scene}</div>
    </div>
  );
}

/* --- Individual noise-card designs --- */

const cardBase = {
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 10px 40px rgba(0,0,0,0.55), 0 0 24px rgba(120,180,255,0.18), inset 0 1px 0 rgba(255,255,255,0.10)",
  border: "1px solid rgba(150,200,255,0.30)",
  background: "rgba(15,25,45,0.55)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
};

function CardBody({ kind }) {
  switch (kind) {
    case "email":     return <EmailCard />;
    case "twitter":   return <TwitterCard />;
    case "facebook":  return <FacebookCard />;
    case "instagram": return <InstagramCard />;
    case "whatsapp":  return <WhatsAppCard />;
    case "adviser":   return <AdviserCard />;
    default: return null;
  }
}

function EmailCard() {
  return (
    <div style={cardBase}>
      <div style={{ background: "#ffffff", padding: 12 }}>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded flex items-center justify-center text-white text-[13px]" style={{ background: "#EA4335" }}>✉</div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-slate-900 truncate">Wealth Advisor · Inbox</div>
            <div className="text-[9px] text-slate-500">Today · 09:17</div>
          </div>
          <div className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">URGENT</div>
        </div>
        <div className="text-[12px] font-bold text-slate-900 leading-tight">Exclusive 40% returns — limited time</div>
        <div className="text-[10px] text-slate-600 leading-tight mt-1">Click below to secure your allocation before Friday.</div>
        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-blue-600 underline decoration-dotted">
          bit.ly/invest-40 <span className="text-red-600">⚠ suspicious link</span>
        </div>
      </div>
    </div>
  );
}

function TwitterCard() {
  return (
    <div style={cardBase}>
      <div style={{ background: "#0F1419", padding: 12 }}>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-slate-500 to-slate-700"/>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-white">CryptoKing_ZA <span className="text-slate-500 font-normal">@ck_za · 2h</span></div>
          </div>
          <div className="text-[14px] text-white font-bold">𝕏</div>
        </div>
        <div className="text-[11px] text-white leading-tight">🚀 Just x10'd my portfolio with this platform. DM me — link in bio 👇</div>
        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-sky-400 underline">
          t.co/xY9k2z <span className="text-amber-400">⚠ shortened link</span>
        </div>
        <div className="flex gap-4 mt-2 text-[9px] text-slate-500">
          <span>💬 214</span><span>🔁 1.2K</span><span>♥ 8.4K</span>
        </div>
      </div>
    </div>
  );
}

function FacebookCard() {
  return (
    <div style={cardBase}>
      <div style={{ background: "#ffffff", padding: 12 }}>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-[14px]" style={{ background: "#1877F2" }}>f</div>
          <div className="flex-1">
            <div className="text-[11px] font-bold text-slate-900">Financial Freedom SA</div>
            <div className="text-[9px] text-slate-500">Sponsored · <span className="text-red-600">⚠ unverified page</span></div>
          </div>
        </div>
        <div className="text-[11px] text-slate-800 leading-tight">"I made R120,000 in 3 weeks — anyone can do it."</div>
        <div className="mt-1.5 rounded overflow-hidden" style={{ height: 60, background: "linear-gradient(135deg,#22c55e,#0ea5e9)" }}/>
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-blue-600 underline">
          freedom-sa.link/join <span className="text-red-600">⚠ scam pattern</span>
        </div>
      </div>
    </div>
  );
}

function InstagramCard() {
  return (
    <div style={cardBase}>
      <div style={{ background: "#000", padding: 0 }}>
        <div className="flex items-center gap-2 p-2">
          <div className="w-6 h-6 rounded-full" style={{ background: "conic-gradient(from 180deg, #F58529, #DD2A7B, #8134AF, #515BD4, #F58529)" }}/>
          <div className="text-[11px] text-white font-semibold flex items-center gap-1">wealth_guru_sa <span className="text-blue-400">✓</span></div>
        </div>
        <div className="relative" style={{ height: 90, background: "linear-gradient(135deg,#f59e0b,#ec4899,#8b5cf6)" }}>
          {/* fake portrait */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/20 border-2 border-white/40"/>
          </div>
          {/* AI-generated warning badge */}
          <div className="absolute top-1.5 right-1.5 flex items-center gap-1 bg-black/70 rounded px-1.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500"/>
            <span className="text-[8px] text-white uppercase tracking-widest font-bold">AI generated</span>
          </div>
        </div>
        <div className="p-2">
          <div className="text-[10px] text-white leading-tight">"Retire at 35 — my system works."</div>
          <div className="text-[9px] text-slate-500 mt-0.5 flex gap-3">
            <span>♥ 42.1K</span><span>💬 3.2K</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function WhatsAppCard() {
  return (
    <div style={cardBase}>
      <div style={{ background: "#0B141A", padding: 12 }}>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[13px]" style={{ background: "#25D366" }}>✆</div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-white">+27 82 ••• 4471</div>
            <div className="text-[9px] text-slate-400">unknown contact</div>
          </div>
        </div>
        <div className="rounded-lg p-2 max-w-[92%]" style={{ background: "#005C4B" }}>
          <div className="text-[10.5px] text-white leading-tight">Hi Nomsa 👋 I'm your new financial adviser. Please confirm your bank OTP so I can complete your onboarding.</div>
          <div className="text-[8.5px] text-slate-300 mt-0.5 text-right">10:24 ✓✓</div>
        </div>
        <div className="mt-1.5 flex items-center gap-1 text-[9.5px] text-red-400 font-semibold">
          ⚠ Message pattern: OTP harvesting
        </div>
      </div>
    </div>
  );
}

function AdviserCard() {
  return (
    <div style={cardBase}>
      <div className="relative" style={{ background: "#0F172A", padding: 8 }}>
        <div className="relative rounded-lg overflow-hidden" style={{ height: 100, background: "linear-gradient(160deg,#1e293b,#0f172a)" }}>
          {/* fake face silhouette */}
          <div className="absolute inset-0 flex items-end justify-center pb-1">
            <div className="w-20 h-16 rounded-t-full" style={{ background: "linear-gradient(180deg,#334155,#1e293b)" }}/>
          </div>
          <div className="absolute top-1 left-1 flex items-center gap-1 bg-black/60 rounded px-1.5 py-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"/>
            <span className="text-[8.5px] text-white uppercase tracking-widest font-bold">LIVE</span>
          </div>
          <div className="absolute bottom-1 right-1 flex items-center gap-1 bg-amber-500/90 rounded px-1.5 py-0.5">
            <span className="text-[8.5px] text-black uppercase tracking-widest font-bold">⚠ Deepfake?</span>
          </div>
        </div>
        <div className="mt-1.5 flex items-center justify-between">
          <div className="text-[10.5px] text-white font-semibold">"Trusted" Adviser</div>
          <div className="text-[9px] text-slate-400">calling…</div>
        </div>
      </div>
    </div>
  );
}
